import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import Loading from '../components/Loading';
import Alert from '../components/Alert';
import Pagination from '../components/Pagination';
import CustomerModal from '../components/CustomerModal';
import { useAuth } from '../context/AuthContext';
import { 
  UserPlus, 
  Search, 
  Pencil, 
  Trash2,
  Users 
} from 'lucide-react';
import './Customers.css';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Local input state
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const searchInputRef = useRef(null); // Reference to search input
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, search]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: {
          page: pagination.page,
          limit: 20,
          search
        }
      });
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleCreateOrUpdate = async (customerData) => {
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer._id}`, customerData);
        setSuccess('Customer updated successfully');
      } else {
        await api.post('/customers', customerData);
        setSuccess('Customer created successfully');
      }
      setShowModal(false);
      setEditingCustomer(null);
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this customer?')) {
      return;
    }

    try {
      await api.delete(`/customers/${id}`);
      setSuccess('Customer deactivated successfully');
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete customer');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCustomer(null);
  };

  // if (loading && customers.length === 0) return <Loading />;

  return (
    <div>
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Users size={28} /> Customers
        </h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={18} /> Add Customer
          </div>
        </button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Search */}
      <div className="card">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
              <Search size={18} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="form-control"
              placeholder="Search by name, phone, or email..."
              value={searchInput}
              onChange={handleSearch}
              style={{ paddingLeft: '40px', fontSize: '15px' }}
            />
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="card">
        <div className="card-header">
          All Customers ({pagination.total})
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>City</th>
                <th>State</th>
                <th>GST Number</th>
                <th>Place of Supply</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.length > 0 ? (
                customers.map(customer => (
                  <tr key={customer._id}>
                    <td>{customer.name}</td>
                    <td>{customer.phone}</td>
                    <td>{customer.email || '-'}</td>
                    <td>{customer.city || '-'}</td>
                    <td>{customer.state || '-'}</td>
                    <td>{customer.gstNumber || '-'}</td>
                    <td>{customer.placeOfSupply}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                        <button
                          className="btn btn-primary btn-small"
                          onClick={() => handleEdit(customer)}
                          style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDelete(customer._id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        )}
      </div>

      <CustomerModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSubmit={handleCreateOrUpdate}
        initialData={editingCustomer}
      />
    </div>
  );
};

export default Customers;
